import React, { useState, useEffect, useCallback } from 'react';
import classnames from 'classnames';
import { useRouter } from 'found';
import isEmpty from 'lodash/isEmpty';

import { Button, Container } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Translate, LoadingOverlay } from '@hydra/common/components';
import { ErrorModal } from '@hydra/common/error';
import { useUserContext } from '@hydra/common/context';
import SelectSearchEngine from '@hydra/routes/serp/components/SelectSearchEngine';
import SelectKeywords from '@hydra/routes/serp/components/SelectKeywords';
import SelectTags from '@hydra/routes/serp/components/SelectTags';
import SelectGeoLocation from '@hydra/routes/serp/components/SelectGeoLocation';
import RankedKeywords from '@hydra/routes/serp/components/RankedKeywords';
import AddKeywordsBar from '@hydra/routes/serp/components/AddKeywordsBar';
import AddKeywordsPageHeader from '@hydra/routes/serp/components/AddKeywordsPageHeader';
import AddKeywordsUpgrade from '@hydra/routes/serp/components/AddKeywordsUpgrade';

import * as serpSelector from '@hydra/routes/serp/modules/serpSelector';
import { isKeywordIncluded } from '@hydra/routes/serp/utils/keywordsDataUtil';

import * as fetcher from '@hydra/utils/fetcher';
import { sendEvent } from '@hydra/utils/gtm';
import { getIsDudaUser } from '@hydra/utils/userUtil';
import { useI18n } from '@hydra/common/customHooks';

import { AddKeywordsPageData, SerpGeoLocation } from '@hydra/types/serp';

const useStyles = makeStyles(theme => ({
  container: {
    paddingTop: theme.spacing(2)
  },
  fullWidth: { width: '100%' },
  btn: {
    width: '100%',
    marginTop: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      width: 'inherit',
      marginTop: 'auto',
      marginLeft: theme.spacing(2)
    }
  },
  btnHover: {
    '&:hover, &:active, &:focus': {
      color: theme.palette.primary.contrastText
    }
  },
  flex: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('sm')]: { flexDirection: 'row' }
  },
  marginTop: {
    marginTop: theme.spacing(2)
  },
  tags: {
    width: '100%',
    zIndex: theme.zIndex.speedDial
  },
  labelBtn: {
    whiteSpace: 'nowrap'
  },
  bgColor: {
    backgroundColor: theme.palette.background.secondary,
    flex: 1
  },
  rankedKeywords: {
    marginBottom: theme.spacing(8)
  }
}));

interface AddKeywordsPageProps {
  data: AddKeywordsPageData
}

export default function AddKeywordsPage ({ data }: AddKeywordsPageProps) {
  const { locale } = useI18n();
  const classes = useStyles();
  const { match: { params }, router } = useRouter();
  const { maxKeywordsPerProject, keywordsUpgradeDetails } = data;
  const { url, projectKeywords, keywordsCount, projectTags, countries, lastAddedKeyword, projectView } = {
    url: data.url,
    projectKeywords: Object.values(data.projectKeywords),
    keywordsCount: data.keywordsCount,
    projectTags: data.projectTags,
    countries: data.countries,
    lastAddedKeyword: serpSelector.getLastAddedKeyword(data),
    projectView: data.projectView
  };

  const path = projectView?.filters?.[0]?.value;
  const reviewUrlWithPath = path ? `${url}/${path}` : url;

  const { user } = useUserContext();
  const maxKeywords = user?.maxKeywords || 0;
  const keywordsLeft = maxKeywordsPerProject
    ? Math.max(0, maxKeywordsPerProject - projectKeywords.length)
    : Math.max(0, maxKeywords - keywordsCount);

  const [isLoading, setIsLoading] = useState(false);
  const [isSearchEngineSelected, setIsSearchEngineSelected] = useState(false);
  const [selectedKeywords, setSelectedKeywords] = useState<string[]>([]);
  const [searchEngine, setSearchEngine] = useState<{ language?: string, country?: string }>({});
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedGeoLocation, setSelectedGeoLocation] = useState<{ locName?: string, locType?: string, nameLabel?: string, typeLabel?: string }>({});
  const [error, setError] = useState(false);

  useEffect(() => {
    if (params && params.domain) {
      setIsSearchEngineSelected(true);
    }
  }, [params]);

  const selectKeywords = (keyword: string[]) => setSelectedKeywords(Array.from(new Set(selectedKeywords.concat(keyword))));

  const unSelectKeywords = (keywords: string[]) => setSelectedKeywords(keywords);

  const selectTags = (tags: string[]) => setSelectedTags(tags);

  const selectGeoLocation = (geoLocation: SerpGeoLocation) => {
    sendEvent('keywordtool-addkeyword-city-input');
    setSelectedGeoLocation(geoLocation || {});
  };

  const handleVerifyReset = () => {
    sendEvent('keywordtool-addkeyword-changedomain-btn');
    if (selectedKeywords.length > 0) {
      if (window.confirm('You are about to reset this page. Are you sure you want to do this?')) {
        resetPage();
      } else {
        return null;
      }
    } else {
      resetPage();
    }
  };

  const resetPage = () => {
    setIsSearchEngineSelected(false);
    router.push(`/${locale}/serp/${encodeURIComponent(url)}/add`);
  };

  const renderDomain = useCallback(() => {
    const language = searchEngine && searchEngine.language && searchEngine.language !== 'default' ? '_' + searchEngine.language : '';
    const country = searchEngine && searchEngine.country ? searchEngine.country : '';

    return `${country}${language}`;
  }, [searchEngine]);

  const handleAddKeywordsToProject = async () => {
    const { country, language } = searchEngine;
    const { locName, locType } = selectedGeoLocation;
    const keywords = selectedKeywords
      .filter(selectedKeyword => !isKeywordIncluded(projectKeywords, { keyword: selectedKeyword, country, language, locName, locType }));
    sendEvent('keywordtool-addkeyword-submit-btn', { addKeywordsCount: keywords.length });

    if (keywords.length <= 0) {
      return;
    }

    setIsLoading(true);
    try {
      await fetcher.post(`/api/serp/keywords/${encodeURIComponent(url)}`, {
        json: {
          keywords,
          country,
          language,
          addedTags: selectedTags,
          geoLocation: selectedGeoLocation
        }
      });
      window.location.href = `/${locale}/serp/${encodeURIComponent(url)}`;
    } catch (err) {
      setIsLoading(false);
      setError(true);
    }
  };

  const createOption = (label: string) => ({ label, value: label });

  const keywordsProvision = keywordsLeft - selectedKeywords.length;

  const applyReady = (
    selectedKeywords.length > 0 &&
    keywordsProvision >= 0 &&
    !isEmpty(searchEngine)
  );

  const handleConfirmClick = useCallback(() => {
    sendEvent('keywordtool-selectdomain-confirm-btn');
    window.location.href = `/${locale}/serp/${encodeURIComponent(url)}/add/${renderDomain()}`;
  }, [locale, url, renderDomain]);

  if (keywordsLeft <= 0) {
    const isDudaUser = getIsDudaUser(user);
    const AddKeywordsUpgradeProps = {
      ...(isDudaUser) ? { upgradeDetails: keywordsUpgradeDetails } : { upgradeLink: `/${locale}/user/plan` }
    };

    return (
      <>
        <AddKeywordsPageHeader url={url} />
        <AddKeywordsUpgrade {...AddKeywordsUpgradeProps} />
      </>
    );
  }

  return (
    <>
      <ErrorModal error={error} setError={setError} />
      <LoadingOverlay isLoading={isLoading} />
      <AddKeywordsPageHeader url={url} />
      <div className={classes.bgColor}>
        <Container maxWidth='lg' className={classes.container}>
          <div className={classes.flex}>
            <div className={classes.fullWidth}>
              <SelectSearchEngine
                isDisabled={isSearchEngineSelected}
                routeParam={params && params.domain ? params.domain : ''}
                setSearchEngine={setSearchEngine}
                searchEngine={searchEngine}
                countries={countries}
                lastAddedKeyword={lastAddedKeyword}
              />
            </div>
            <div className={classes.btn}>
              {isSearchEngineSelected
                ? (
                  <Button
                    onClick={handleVerifyReset}
                    variant='outlined'
                    classes={{ label: classes.labelBtn }}
                  >
                    <Translate content='change_domain' />
                  </Button>
                  )
                : (
                  <Button
                    onClick={handleConfirmClick}
                    variant='contained'
                    color='primary'
                    disabled={searchEngine && Object.keys(searchEngine).length === 0}
                    className={classnames(classes.btnHover, 'nr-confirm-search-engine')}
                  >
                    <Translate content='confirm' />
                  </Button>
                  )}
            </div>
          </div>
          {isSearchEngineSelected
            ? (
              <div className={classes.marginTop}>
                <SelectGeoLocation
                  country={searchEngine.country}
                  selectGeoLocation={selectGeoLocation}
                  selectedGeoLocation={selectedGeoLocation}
                />
              </div>
              )
            : null}
          {isSearchEngineSelected &&
            (
              <>
                <div className={classes.marginTop}>
                  <SelectKeywords
                    keywordsLeft={keywordsLeft}
                    selectedKeywords={selectedKeywords}
                    selectKeywords={selectKeywords}
                    unSelectKeywords={unSelectKeywords}
                    createOption={createOption}
                    keywordsProvision={keywordsProvision}
                  />
                  <div className={classes.marginTop}>
                    <div className={classes.tags}>
                      <SelectTags
                        createOption={createOption}
                        selectTags={selectTags}
                        tags={selectedTags}
                        projectTags={projectTags}
                      />
                    </div>
                  </div>
                </div>
                <div className={classes.rankedKeywords}>
                  <RankedKeywords
                    keywordsLeft={keywordsLeft}
                    selectKeywords={selectKeywords}
                    selectedKeywords={selectedKeywords}
                    selectedGeoLocation={selectedGeoLocation}
                    routeParams={params}
                    keywords={projectKeywords}
                    url={reviewUrlWithPath}
                  />
                </div>
              </>
            )}
        </Container>
      </div>
      {isSearchEngineSelected && (
        <AddKeywordsBar
          keywords={selectedKeywords}
          disabled={!applyReady}
          handleClick={handleAddKeywordsToProject}
        />
      )}
    </>
  );
}
